import React, { useState, useEffect } from 'react'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CButton,
  CButtonGroup,
  CListGroup,
  CListGroupItem,
  CBadge,
  CAlert,
  CSpinner,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CProgress,
  CToast,
  CToaster,
  CPagination,
  CPaginationItem,
  CFormSelect,
  CFormLabel
} from '@coreui/react'
import { toast, ToastContainer } from 'react-toastify'
import Swal from 'sweetalert2'
import 'react-toastify/dist/ReactToastify.css'
import { CIcon } from '@coreui/icons-react'
import { 
  cilBell, 
  cilBellExclamation, 
  cilCheckCircle, 
  cilInfo, 
  cilWarning, 
  cilClock,
  cilSettings,
  cilTrash,
  cilReload,
  cilEnvelopeOpen,
  cilEnvelopeClosed,
  cilUser,
  cilLockLocked,
  cilStar,
  cilChevronLeft,
  cilChevronRight
} from '@coreui/icons'
import { useAuth } from '../utils/AuthContext'
import { fetchNotifications, markNotificationAsRead, markNotificationAsUnread, markAllNotificationsAsRead, getUnreadCount, deleteNotification } from '../../utils/api'

const Notifications = () => {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingAction, setIsLoadingAction] = useState(false)
  const [filter, setFilter] = useState('all') // 'all', 'unread', 'read'
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(5)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    loadNotifications()
    loadUnreadCount()
  }, [filter, currentPage, perPage])

  const loadNotifications = async () => {
    if (!user?.id) return
    
    setIsLoading(true)
    try {
      const response = await fetchNotifications(user.id, filter === 'unread', currentPage, perPage)
      if (response.status === 'success') {
        setNotifications(response.data || [])
        
        // Handle pagination metadata
        if (response.pagination) {
          setTotalItems(response.pagination.total || 0)
          setTotalPages(response.pagination.total_pages || 0)
        } else {
          // Fallback if pagination data is not provided
          setTotalItems(response.data?.length || 0)
          setTotalPages(1)
        }
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
      toast.error('❌ Failed to load notifications. Please try again.', {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const loadUnreadCount = async () => {
    if (!user?.id) return
    
    try {
      const response = await getUnreadCount(user.id)
      if (response.status === 'success') {
        setUnreadCount(response.data.count)
      }
    } catch (error) {
      console.error('Error loading unread count:', error)
    }
  }

  const handleMarkAsRead = async (notificationId) => {
    if (!user?.id) return
    
    setIsLoadingAction(true)
    try {
      const response = await markNotificationAsRead(notificationId, user.id)
      if (response.status === 'success') {
        toast.success('✅ Notification marked as read!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
        loadNotifications()
        loadUnreadCount()
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
      toast.error('❌ Failed to mark notification as read. Please try again.', {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handleMarkAsUnread = async (notificationId) => {
    if (!user?.id) return
    
    setIsLoadingAction(true)
    try {
      const response = await markNotificationAsUnread(notificationId, user.id)
      if (response.status === 'success') {
        toast.success('📬 Notification marked as unread!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
        loadNotifications()
        loadUnreadCount()
      }
    } catch (error) {
      console.error('Error marking notification as unread:', error)
      toast.error('❌ Failed to mark notification as unread. Please try again.', {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handleDeleteNotification = async (notificationId) => {
    if (!user?.id) return
    
    // Ask for confirmation using SweetAlert
    const result = await Swal.fire({
      title: 'Delete Notification?',
      text: 'Are you sure you want to delete this notification? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    })
    
    if (!result.isConfirmed) {
      return
    }
    
    setIsLoadingAction(true)
    try {
      const response = await deleteNotification(notificationId, user.id)
      if (response.status === 'success') {
        toast.success('🗑️ Notification deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
        loadNotifications()
        loadUnreadCount()
      }
    } catch (error) {
      console.error('Error deleting notification:', error)
      toast.error('❌ Failed to delete notification. Please try again.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return
    
    setIsLoadingAction(true)
    try {
      const response = await markAllNotificationsAsRead(user.id)
      if (response.status === 'success') {
        toast.success('✅ All notifications marked as read!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
        loadNotifications()
        loadUnreadCount()
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      toast.error('❌ Failed to mark all notifications as read. Please try again.', {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset to first page when changing items per page
  }

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter)
    setCurrentPage(1) // Reset to first page when changing filter
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'welcome':
        return cilStar
      case 'profile_update':
        return cilUser
      case 'password_change':
        return cilLockLocked
      case 'warning':
        return cilWarning
      case 'success':
        return cilCheckCircle
      case 'system':
        return cilSettings
      default:
        return cilInfo
    }
  }

  const getNotificationColor = (type) => {
    switch (type) {
      case 'welcome':
        return 'primary'
      case 'profile_update':
        return 'info'
      case 'password_change':
        return 'warning'
      case 'warning':
        return 'danger'
      case 'success':
        return 'success'
      case 'system':
        return 'secondary'
      default:
        return 'info'
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 1) {
      return 'Today'
    } else if (diffDays === 2) {
      return 'Yesterday'
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification)
    setShowModal(true)
    
    // Mark as read if it's unread
    if (!notification.is_read) {
      handleMarkAsRead(notification.id)
    }
  }



  return (
    <>
      <CRow>
        <CCol xs={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Notifications</strong>
                  {unreadCount > 0 && (
                    <CBadge color="danger" className="ms-2">
                      {unreadCount} unread
                    </CBadge>
                  )}
                  {totalItems > 0 && (
                    <small className="text-muted ms-2">
                      ({totalItems} total)
                    </small>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <CButtonGroup>
                    <CButton
                      color={filter === 'all' ? 'primary' : 'outline-primary'}
                      onClick={() => handleFilterChange('all')}
                      size="sm"
                    >
                      All
                    </CButton>
                    <CButton
                      color={filter === 'unread' ? 'primary' : 'outline-primary'}
                      onClick={() => handleFilterChange('unread')}
                      size="sm"
                    >
                      Unread
                    </CButton>
                    <CButton
                      color={filter === 'read' ? 'primary' : 'outline-primary'}
                      onClick={() => handleFilterChange('read')}
                      size="sm"
                    >
                      Read
                    </CButton>
                  </CButtonGroup>
                  
                  <CDropdown>
                    <CDropdownToggle color="secondary" size="sm">
                      <CIcon icon={cilSettings} />
                    </CDropdownToggle>
                    <CDropdownMenu>
                      <CDropdownItem
                        onClick={handleMarkAllAsRead}
                        disabled={isLoadingAction || unreadCount === 0}
                      >
                        <CIcon icon={cilEnvelopeOpen} className="me-2" />
                        Mark All as Read
                      </CDropdownItem>
                      <CDropdownItem onClick={() => {
                        setCurrentPage(1)
                        loadNotifications()
                      }}>
                        <CIcon icon={cilReload} className="me-2" />
                        Refresh
                      </CDropdownItem>
                    </CDropdownMenu>
                  </CDropdown>
                </div>
              </div>
            </CCardHeader>
            <CCardBody>
              {/* Per-page selector */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <CFormLabel htmlFor="perPageSelect" className="mb-0">
                    Items per page:
                  </CFormLabel>
                  <CFormSelect
                    id="perPageSelect"
                    value={perPage}
                    onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                    size="sm"
                    style={{ width: 'auto' }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </CFormSelect>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-5">
                  <CSpinner color="primary" />
                  <p className="text-muted mt-2">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-5">
                  <CIcon icon={cilBell} size="3xl" className="text-muted mb-3" />
                  <h5 className="text-muted">No notifications</h5>
                  <p className="text-muted">
                    {filter === 'unread' ? 'You have no unread notifications' : 'You have no notifications yet'}
                  </p>
                </div>
              ) : (
                <CListGroup flush>
                  {notifications.map((notification) => (
                    <CListGroupItem
                      key={notification.id}
                      className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                      style={{ 
                        backgroundColor: !notification.is_read ? '#f8f9fa' : 'transparent',
                        borderLeft: !notification.is_read ? '4px solid #0d6efd' : '4px solid transparent'
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div className="me-3">
                          <CBadge
                            color={getNotificationColor(notification.type)}
                            className="rounded-circle p-2"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <CIcon icon={getNotificationIcon(notification.type)} />
                          </CBadge>
                        </div>
                        <div 
                          className="flex-grow-1 min-width-0" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className={`mb-0 text-truncate pe-2 ${!notification.is_read ? 'fw-bold' : ''}`}>
                              {notification.title}
                            </h6>
                            <small className="text-muted nowrap">
                              {formatDate(notification.created_at)}
                            </small>
                          </div>
                          <p className={`text-muted mb-0 small ${!notification.is_read ? 'fw-bold' : ''}`}>
                            {notification.message.length > 100 
                              ? `${notification.message.substring(0, 100)}...` 
                              : notification.message
                            }
                          </p>
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <CBadge 
                              color={getNotificationColor(notification.type)} 
                              variant="outline"
                              className="small"
                            >
                              {notification.type.replace('_', ' ')}
                            </CBadge>
                            {!notification.is_read && (
                              <CBadge color="primary" className="small">
                                New
                              </CBadge>
                            )}
                          </div>
                        </div>
                        <div className="ms-3 d-flex flex-row gap-1">
                          <CButton
                            color={notification.is_read ? "outline-secondary" : "outline-primary"}
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (notification.is_read) {
                                handleMarkAsUnread(notification.id)
                              } else {
                                handleMarkAsRead(notification.id)
                              }
                            }}
                            disabled={isLoadingAction}
                            title={notification.is_read ? "Mark as unread" : "Mark as read"}
                          >
                            <CIcon 
                              icon={notification.is_read ? cilEnvelopeOpen : cilEnvelopeClosed} 
                              size="sm" 
                            />
                          </CButton>
                          <CButton
                            color="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteNotification(notification.id)
                            }}
                            disabled={isLoadingAction}
                            title="Delete notification"
                          >
                            <CIcon icon={cilTrash} size="sm" />
                          </CButton>
                        </div>
                      </div>
                    </CListGroupItem>
                  ))}
                </CListGroup>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div>
                    <small className="text-muted">
                      Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} notifications
                    </small>
                  </div>
                  
                  <CPagination aria-label="Notifications pagination">
                    <CPaginationItem
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <CIcon icon={cilChevronLeft} />
                    </CPaginationItem>
                    
                    {/* Page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 || 
                        page === totalPages || 
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <CPaginationItem
                            key={page}
                            active={page === currentPage}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </CPaginationItem>
                        )
                      } else if (
                        page === currentPage - 3 || 
                        page === currentPage + 3
                      ) {
                        return (
                          <CPaginationItem key={page} disabled>
                            ...
                          </CPaginationItem>
                        )
                      }
                      return null
                    })}
                    
                    <CPaginationItem
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <CIcon icon={cilChevronRight} />
                    </CPaginationItem>
                  </CPagination>
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Notification Detail Modal */}
      <CModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        aria-labelledby="notification-modal-title"
        size="lg"
      >
        <CModalHeader>
          <CModalTitle id="notification-modal-title">
            {selectedNotification?.title}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedNotification && (
            <>
              <div className="mb-3">
                <CBadge
                  color={getNotificationColor(selectedNotification.type)}
                  className="me-2"
                >
                  <CIcon icon={getNotificationIcon(selectedNotification.type)} className="me-1" />
                  {selectedNotification.type.replace('_', ' ')}
                </CBadge>
                <small className="text-muted">
                  {new Date(selectedNotification.created_at).toLocaleString()}
                </small>
              </div>
              
              <div className="notification-content">
                <p>{selectedNotification.message}</p>
              </div>
              
              {selectedNotification.action_url && selectedNotification.action_text && (
                <div className="mt-3">
                  <CButton
                    color="primary"
                    onClick={() => {
                      window.location.href = selectedNotification.action_url
                    }}
                  >
                    {selectedNotification.action_text}
                  </CButton>
                </div>
              )}
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <div className="d-flex justify-content-between w-100">
            <div className="d-flex gap-2">
              <CButton
                color="success"
                variant={selectedNotification?.is_read ? "solid" : "outline"}
                onClick={() => {
                  handleMarkAsRead(selectedNotification.id)
                  setShowModal(false)
                }}
                disabled={isLoadingAction || selectedNotification?.is_read === true}
              >
                <CIcon icon={cilEnvelopeOpen} className="me-2" />
                Mark as Read
              </CButton>
              <CButton
                color="warning"
                variant={selectedNotification?.is_read ? "outline" : "solid"}
                onClick={() => {
                  handleMarkAsUnread(selectedNotification.id)
                  setShowModal(false)
                }}
                disabled={isLoadingAction || selectedNotification?.is_read === false}
              >
                <CIcon icon={cilEnvelopeClosed} className="me-2" />
                Mark as Unread
              </CButton>
              <CButton
                color="danger"
                variant="outline"
                onClick={() => {
                  handleDeleteNotification(selectedNotification.id)
                  setShowModal(false)
                }}
                disabled={isLoadingAction}
              >
                <CIcon icon={cilTrash} className="me-2" />
                Delete
              </CButton>
            </div>
            <CButton 
              color="secondary" 
              onClick={() => setShowModal(false)}
            >
              Close
            </CButton>
          </div>
        </CModalFooter>
      </CModal>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  )
}

export default Notifications 